import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import {
    QuoteButton,
    RentButton,
    ReserveButton,
    SelectButton,
    CallForAvailability,
} from "@/components/units/actions";
import { QuoteType } from "@/types/enums";
import { useRentalAction } from "@/hooks/useRentalAction";
import { useFacility } from "@/hooks/useFacility";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility";
import { useReservationAction } from "@/hooks/useReservationAction";
import { UnitGroup } from "@/types";

interface TriggerButtonsProps {
    unitGroup: UnitGroup;
    selectedPromo: number;
}

export const TriggerButtons = ({
    unitGroup,
    selectedPromo,
}: TriggerButtonsProps) => {
    const displayOptions = useDisplayOptionContext();
    const { openVbp, openRentalFunnel } = useRentalAction(
        unitGroup,
        selectedPromo,
    );
    const { disableSoftReservation } = displayOptions;
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);
    const {
        hasMultipleSubGroups,
        hasAvailableUnits,
        canReserve,
        canRent,
        shouldShowSelectButton,
    } = useTriggerButtonVisibility(unitGroup);

    const { openReservationFunnel, label } = useReservationAction(
        unitGroup,
        selectedPromo,
    );

    const phone = facility?.phone || "";
    const quoteType = facility?.settings.quote_type;

    const handleRentalClick = () => {
        if (hasMultipleSubGroups) {
            return openVbp();
        } else {
            return openRentalFunnel();
        }
    };

    /*
     * If the unit group has available units, we will either show the
     * Select button, or the individual action buttons.
     */
    if (hasAvailableUnits) {
        if (shouldShowSelectButton()) {
            return (
                <SelectButton
                    unitGroup={unitGroup}
                    selectedPromo={selectedPromo}
                />
            );
        }
        return (
            <div>
                {canRent && (
                    <RentButton handleRentalClick={handleRentalClick} />
                )}
                {canReserve && (
                    <ReserveButton
                        onReservationClick={openReservationFunnel}
                        label={label}
                    />
                )}
                {/* Safety fallback. */}
                {!canRent && !canReserve && (
                    <>
                        {!disableSoftReservation &&
                        quoteType === QuoteType.RESERVE ? (
                            <QuoteButton
                                unitGroup={unitGroup}
                                selectedPromo={selectedPromo}
                            />
                        ) : (
                            <CallForAvailability phone={phone} />
                        )}
                    </>
                )}
            </div>
        );
    }

    /*
     * No available units. We will either show the Quote button,
     * or Call for Availability.
     *
     * At this point, 'reservation_type' is NOT 'click_to_call', and
     * there are no available units.
     */
    if (disableSoftReservation) {
        // console.log(
        //     "Soft reservation disabled, no available units. Showing Call for Availability",
        // );
        return <CallForAvailability phone={phone} />;
    } else {
        // console.log(
        //     quoteType === QuoteType.RESERVE
        //         ? "Quote type is reserve, showing Quote button"
        //         : "Quote type is call, showing Call for Availability",
        // );
        return quoteType === QuoteType.RESERVE ? (
            <QuoteButton unitGroup={unitGroup} selectedPromo={selectedPromo} />
        ) : (
            <CallForAvailability phone={phone} />
        );
    }
};

TriggerButtons.displayName = "TriggerButtons";
